import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {api} from "../api";
import {
    ConnectData, DataParams, GameBasic, NavigationParams, NpsData, NumberScaleData,
    OrderData,
    SelectData, TextData, Version,
} from "../../types/admin";

export type AdminQuestionState = {
    loaded: boolean
    error: string | null
    active_languages: string[]
    title: string;
    type: string;
    type_display: string;
    played: number;
    max_points: number;
    average_points: number;
    right_answers: number;
    wrong_answers: number;
    multiple: boolean;
    game: GameBasic | null;
    connect_data: ConnectData;
    select_data: SelectData[];
    order_data: OrderData;
    text_data: TextData;
    number_scale_data: NumberScaleData[];
    nps_data?: NpsData;
    versions: Version[];
    current_version_id: string;
}

const initialState: AdminQuestionState = {
    loaded: false,
    error: null,
    active_languages: [],
    title: '',
    type: '',
    type_display: '',
    played: 0,
    max_points: 0,
    average_points: 0,
    right_answers: 0,
    wrong_answers: 0,
    multiple: false,
    game: null,
    connect_data: {
        options1: [],
        options2: [],
        connections: [],
    },
    select_data: [],
    order_data: {
        options: [],
        connections: [],
    },
    text_data: {
        labels: [],
        data: [],
    },
    number_scale_data: [],
    current_version_id: '',
    versions: [],
}

export type QuestionDataParams = {
    question_id: string;
    version_id?: string;
}

type CombinedParams = QuestionDataParams & DataParams;

export const extractParams = (params: CombinedParams): NavigationParams => {
    let _params: any = {};
    if (params.language) _params.language = params.language;
    if (params.team) _params.team = params.team;
    if (params.date_start) _params.date_start = params.date_start;
    if (params.date_end) _params.date_end = params.date_end;
    if (params.start) _params.start = params.start;
    if (params.end) _params.end = params.end;
    if (params.preset) _params.preset = params.preset;
    return _params;
}

export const getAdminQuestion = createAsyncThunk(
    'adminQuestion/getAdminQuestion',
    async (params: CombinedParams, thunkAPI) => {
        const _params = extractParams(params);
        const {question_id, version_id} = params;
        let endpoint = `/API/admin/questions/${question_id}/`;
        if (version_id) {
            endpoint = `/API/admin/questions/${question_id}/versions/${version_id}/`;
        }
        try {
            const response = await api.get(endpoint, _params);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const slice = createSlice({
    name: 'adminQuestion',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAdminQuestion.pending, (state) => {
                state.loaded = false;
                state.error = null;
            })
            .addCase(getAdminQuestion.fulfilled, (state, action) => {
                state.loaded = true;
                state.error = null;
                Object.assign(state, action.payload as AdminQuestionState);
            })
            .addCase(getAdminQuestion.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
    }
});

export default slice.reducer;
